import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { get, post, del } from '@/lib/api'
import { useDraftStore } from '@/stores/draft.store'
import { useEfficiencyStore, type DraftData } from '@/stores/efficiency.store'

// ==================== Query Keys ====================

export const draftKeys = {
  all: ['drafts'] as const,
  list: () => [...draftKeys.all, 'list'] as const,
  detail: (formType: string) => [...draftKeys.all, formType] as const,
}

// ==================== Constants ====================

const AUTO_SAVE_INTERVAL = 30000 // 30 seconds
const DEBOUNCE_DELAY = 1000 // 1 second debounce for manual saves

// ==================== Hooks ====================

/**
 * 获取所有草稿
 */
export function useAllDrafts() {
  return useQuery({
    queryKey: draftKeys.list(),
    queryFn: () => get<DraftData[]>('/users/me/drafts'),
    staleTime: 60000,
  })
}

/**
 * 获取指定表单的草稿
 */
export function useDraft(formType: string) {
  const { getDraftLocal } = useEfficiencyStore()

  const query = useQuery({
    queryKey: draftKeys.detail(formType),
    queryFn: () => get<DraftData | null>(`/users/me/drafts/${formType}`),
    staleTime: 60000,
  })

  // Return local draft if server data not loaded
  const localDraft = getDraftLocal(formType)

  return {
    ...query,
    data: query.data ?? localDraft,
  }
}

/**
 * 保存草稿
 */
export function useSaveDraft() {
  const queryClient = useQueryClient()
  const { saveDraftLocal } = useEfficiencyStore()

  return useMutation({
    mutationFn: ({ formType, data }: { formType: string; data: Record<string, unknown> }) =>
      post<DraftData>(`/users/me/drafts/${formType}`, { data }),
    onMutate: async ({ formType, data }) => {
      // Optimistic update - save locally immediately
      saveDraftLocal(formType, data)
    },
    onSuccess: (_data, { formType }) => {
      void queryClient.invalidateQueries({ queryKey: draftKeys.detail(formType) })
    },
  })
}

/**
 * 删除草稿
 */
export function useClearDraft() {
  const queryClient = useQueryClient()
  const { clearDraftLocal } = useEfficiencyStore()

  return useMutation({
    mutationFn: (formType: string) => del<null>(`/users/me/drafts/${formType}`),
    onMutate: async (formType) => {
      // Optimistic update
      clearDraftLocal(formType)
    },
    onSuccess: (_data, formType) => {
      void queryClient.invalidateQueries({ queryKey: draftKeys.detail(formType) })
      void queryClient.invalidateQueries({ queryKey: draftKeys.list() })
    },
  })
}

/**
 * 表单草稿管理 Hook
 * 提供自动保存、恢复、清除功能
 */
export function useFormDraft<T extends Record<string, unknown>>(
  formType: string,
  options?: {
    autoSave?: boolean
    onRestore?: (data: T) => void
  }
) {
  const { autoSave = true, onRestore } = options ?? {}
  const { preferences } = useEfficiencyStore()
  const { data: draft, isLoading } = useDraft(formType)
  const { mutate: saveDraft, isPending: isSaving } = useSaveDraft()
  const { mutate: clearDraft } = useClearDraft()

  const lastSavedRef = useRef<string | null>(null)
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Check if auto-save is enabled in preferences
  const isAutoSaveEnabled = autoSave && (preferences?.enableDraftAutoSave ?? true)

  // Save draft function
  const save = useCallback(
    (data: T) => {
      const dataStr = JSON.stringify(data)
      // Only save if data changed
      if (dataStr !== lastSavedRef.current) {
        saveDraft({ formType, data })
        lastSavedRef.current = dataStr
      }
    },
    [formType, saveDraft]
  )

  // Clear draft function
  const clear = useCallback(() => {
    clearDraft(formType)
    lastSavedRef.current = null
  }, [formType, clearDraft])

  // Restore draft function
  const restore = useCallback(() => {
    if (draft && onRestore) {
      onRestore(draft.data as T)
      lastSavedRef.current = JSON.stringify(draft.data)
    }
  }, [draft, onRestore])

  // Auto-save with interval
  const startAutoSave = useCallback(
    (getData: () => T) => {
      if (!isAutoSaveEnabled) {return}

      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current)
      }

      // Start new timer
      autoSaveTimerRef.current = setInterval(() => {
        const data = getData()
        save(data)
      }, AUTO_SAVE_INTERVAL)
    },
    [isAutoSaveEnabled, save]
  )

  const stopAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current)
      autoSaveTimerRef.current = null
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoSave()
    }
  }, [stopAutoSave])

  return {
    draft,
    isLoading,
    isSaving,
    hasDraft: !!draft,
    save,
    clear,
    restore,
    startAutoSave,
    stopAutoSave,
    savedAt: draft?.savedAt,
  }
}

// ==================== Enhanced Draft Hook ====================

export interface UseDraftOptions<T> {
  /** 表单唯一标识 */
  key: string
  /** 初始值 */
  initialValue: T
  /** 自动保存延迟（毫秒），默认 1000ms */
  autoSaveDelay?: number
  /** 是否启用自动保存，默认 true */
  enableAutoSave?: boolean
}

export interface UseDraftReturn<T> {
  /** 当前草稿数据 */
  data: T
  /** 设置草稿数据 */
  setData: React.Dispatch<React.SetStateAction<T>>
  /** 是否有恢复的草稿 */
  hasRestoredDraft: boolean
  /** 清除草稿 */
  clearDraft: () => void
  /** 最后保存时间 */
  lastSavedAt: Date | null
  /** 是否正在保存 */
  isSaving: boolean
  /** 手动保存 */
  save: () => void
}

/**
 * 增强的表单草稿 Hook
 * 提供自动保存、恢复、清除功能
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5
 * 
 * @example
 * ```tsx
 * const { data, setData, hasRestoredDraft, clearDraft, lastSavedAt } = useEnhancedDraft({
 *   key: 'leave_form',
 *   initialValue: { type: 'annual', startDate: '', endDate: '', reason: '' },
 * })
 * ```
 */
export function useEnhancedDraft<T extends object>(
  options: UseDraftOptions<T>
): UseDraftReturn<T> {
  const {
    key,
    initialValue,
    autoSaveDelay = DEBOUNCE_DELAY,
    enableAutoSave = true,
  } = options

  const { preferences } = useEfficiencyStore()
  const { saveDraft: saveDraftToStore, getDraft, clearDraft: clearDraftFromStore } = useDraftStore()

  // State
  const [data, setData] = useState<T>(initialValue)
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Refs
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedDataRef = useRef<string | null>(null)
  const isInitializedRef = useRef(false)

  // Check if auto-save is enabled in preferences
  const isAutoSaveEnabled = enableAutoSave && (preferences?.enableDraftAutoSave ?? true)

  // Initialize - check for existing draft
  useEffect(() => {
    if (isInitializedRef.current) {return}
    isInitializedRef.current = true

    const existingDraft = getDraft<T>(key)
    if (existingDraft) {
      setData(existingDraft.data)
      setLastSavedAt(new Date(existingDraft.savedAt))
      setHasRestoredDraft(true)
      lastSavedDataRef.current = JSON.stringify(existingDraft.data)
    }
  }, [key, getDraft])

  // Save draft function
  const saveDraftInternal = useCallback(() => {
    const dataStr = JSON.stringify(data)
    
    // Only save if data changed
    if (dataStr === lastSavedDataRef.current) {
      return
    }

    // Don't save if data is same as initial value
    if (dataStr === JSON.stringify(initialValue)) {
      return
    }

    setIsSaving(true)
    saveDraftToStore(key, key, data)
    lastSavedDataRef.current = dataStr
    setLastSavedAt(new Date())
    
    // Simulate async save completion
    setTimeout(() => {
      setIsSaving(false)
    }, 100)
  }, [data, key, initialValue, saveDraftToStore])

  // Manual save function
  const save = useCallback(() => {
    saveDraftInternal()
  }, [saveDraftInternal])

  // Clear draft function
  const clearDraft = useCallback(() => {
    clearDraftFromStore(key)
    setLastSavedAt(null)
    setHasRestoredDraft(false)
    lastSavedDataRef.current = null
  }, [key, clearDraftFromStore])

  // Auto-save with 30 second interval (Requirements: 12.1)
  useEffect(() => {
    if (!isAutoSaveEnabled) {return}

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current)
    }

    // Start new timer - save every 30 seconds
    autoSaveTimerRef.current = setInterval(() => {
      saveDraftInternal()
    }, AUTO_SAVE_INTERVAL)

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current)
      }
    }
  }, [isAutoSaveEnabled, saveDraftInternal])

  // Debounced save on data change
  useEffect(() => {
    if (!isAutoSaveEnabled) {return}

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      saveDraftInternal()
    }, autoSaveDelay)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [data, isAutoSaveEnabled, autoSaveDelay, saveDraftInternal])

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      const dataStr = JSON.stringify(data)
      if (dataStr !== lastSavedDataRef.current && dataStr !== JSON.stringify(initialValue)) {
        saveDraftToStore(key, key, data)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [data, key, initialValue, saveDraftToStore])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current)
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    data,
    setData,
    hasRestoredDraft,
    clearDraft,
    lastSavedAt,
    isSaving,
    save,
  }
}
